import { Avatar, Chip } from "@mui/material";
import EditIcon from "../../../assets/icons/edit-fill.svg";
import RevokeIcon from "../../../assets/icons/RevokeUser.svg";

const AdminUsers = ({ users, onEditUser, onDeleteUser, isLoading, isViewer }) => {
  const allowedUserRoles = [
    "admin",
    "owner",
    "manager",
    "viewer",
    "superadmin",
    "developer",
    "support",
  ];
  const normalizeRole = (user) =>
    (user.backendRole || "").toString().trim().toLowerCase();
  const adminUsers = users
    .filter((user) => allowedUserRoles.includes(normalizeRole(user)))
    .sort((first, second) => {
      const aOwner = normalizeRole(first) === "owner" ? 0 : 1;
      const bOwner = normalizeRole(second) === "owner" ? 0 : 1;
      return aOwner - bOwner;
    });

  const getDisplayRole = (user) => {
    const backendRole = (user.backendRole || "").toString().trim().toLowerCase();

    if (backendRole === "admin") {
      return "Admin";
    }

    if (backendRole === "owner") {
      return "Owner";
    }

    if (backendRole === "manager") {
      return "Manager";
    }

    if (backendRole === "viewer") {
      return "Viewer";
    }

    if (backendRole === "superadmin") {
      return "Superadmin";
    }

    if (backendRole === "developer") {
      return "Developer";
    }

    if (backendRole === "support") {
      return "Support";
    }

    return user.role || "Viewer";
  };

  const getRoleBadge = (role) => {
    const isAdmin = role === "Admin";
    const isOwner = role === "Owner";
    const isManager = role === "Manager";
    const isPrimary = isAdmin || isOwner;

    return (
      <Chip
        label={role}
        size="small"
        sx={{
          bgcolor: isAdmin ? "#0A2342" : isOwner ? "rgba(10, 35, 66, 0.5)" : isManager ? "#E5E7EB" : "transparent",
          color: isPrimary ? "white" : "#011A3C",
          border: isAdmin ? "none" : "1px solid #011A3C",
          fontWeight: 500,
          fontSize: "0.75rem",
          height: 24,
          borderRadius: "4px",
        }}
      />
    );
  };

  const getAccessDescription = (role) => {
    switch (role) {
      case "Admin":
        return "Full access";
      case "Manager":
        return "All except billing and account settings";
      case "Viewer":
        return "Only read";
      default:
        return "";
    }
  };

  const getInitials = (name) => {
    if (!name) {
      return "?";
    }

    return name
      .split(" ")
      .map((n) => n[0])
      .join("");
  };

  return (
    <div className="w-full rounded-2xl py-6 px-4 sm:px-7 border border-line bg-white">
      <h2 className="text-lg font-semibold text-primary mb-1">Admin Users</h2>
      <p className="text-xs text-gray-500 mb-6">
        Manage admin access and permission
      </p>

      <div className="space-y-4">
        {!isLoading && adminUsers.length === 0 && (
          <div className="px-4 py-8 border border-dashed border-line rounded-2xl text-sm text-muted text-center">
            No admin users were found for this client.
          </div>
        )}

        {adminUsers.map((user) => {
          const displayRole = getDisplayRole(user);

          return (
            <div
              key={user.id}
              className="flex items-center justify-between px-4 py-4 sm:py-8 border border-line rounded-2xl"
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <Avatar
                  src={user.avatar}
                  alt={user.name}
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: "grey.300",
                    color: "#011A3C",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                  }}
                  className="border border-primary"
                >
                  {!user.avatar && getInitials(user.name)}
                </Avatar>

                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-primary">
                      {user.name}
                    </span>
                    {getRoleBadge(displayRole)}
                    {user.mfaEnabled && (
                      <Chip
                        icon={
                          <svg
                            width="9"
                            height="12"
                            viewBox="0 0 6 8"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M3.25 0.25L0.25 1.41667V3.58333C0.25 4.75 1.41667 6.58333 2.91667 6.91667C4.41667 6.58333 5.58333 4.75 5.58333 3.58333V1.41667L2.91667 0.25H3.25Z"
                              stroke="#011A3C"
                              strokeWidth="0.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        }
                        label="MFA"
                        size="small"
                        sx={{
                          bgcolor: "transparent",
                          border: "1px solid #011A3C",
                          borderColor: "#011A3C",
                          color: "#011A3C",
                          fontWeight: 500,
                          fontSize: "0.75rem",
                          height: 24,
                          borderRadius: "4px",
                        }}
                      />
                    )}
                  </div>
                  <p className="text-sm text-gray-500">{user.email}</p>
                  <p className="text-xs text-gray-400 ">
                    {getAccessDescription(displayRole)}
                  </p>
                </div>
              </div>

              {!isViewer && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onEditUser(user)}
                    className="w-7 h-7 flex items-center justify-center rounded-full border border-primary"
                  >
                    <img src={EditIcon} alt="edit icon" loading="eager" />
                  </button>
                  <button
                    onClick={() => onDeleteUser(user)}
                    className="w-7 h-7 flex items-center justify-center rounded-full border border-alert"
                  >
                    <img src={RevokeIcon} alt="revoke icon" loading="eager" />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminUsers;
